/*
 * Product analytics for the front end application.
 *
 * App modules should import from this module rather than using the
 * underlying provider (PostHog) directly, so the provider remains an
 * implementation detail that can be swapped in one place.
 *
 * Event convention: past-tense snake_case `object_action` event names
 * (e.g. `button_clicked`, `campaign_created`) with snake_case property
 * keys (e.g. `button_name`). Do not add user or organization identifiers
 * to event properties — identifyUser and setOrganization attach those to
 * every event automatically.
 */

import posthog from 'posthog-js';

const { REACT_APP_POSTHOG_HOST, REACT_APP_POSTHOG_PROJECT_TOKEN } = process.env;

let isInitialized = false;

export const initAnalytics = () => {
  if (isInitialized || !REACT_APP_POSTHOG_PROJECT_TOKEN) {
    return;
  }

  posthog.init(REACT_APP_POSTHOG_PROJECT_TOKEN, {
    api_host: REACT_APP_POSTHOG_HOST,
    defaults: '2026-05-30',
  });

  isInitialized = true;
};

export const trackEvent = (eventName, properties = {}) => {
  if (!isInitialized) {
    return;
  }

  posthog.capture(eventName, properties);
};

export const getSessionId = () => {
  if (!isInitialized) {
    return '';
  }

  return posthog.get_session_id();
};

export const identifyUser = (userUuid, properties = {}) => {
  if (!isInitialized) {
    return;
  }

  posthog.identify(userUuid, properties);
};

export const setOrganization = (organizationUuid, properties = {}) => {
  if (!isInitialized) {
    return;
  }

  posthog.register({
    organization_name: properties.name,
    organization_uuid: organizationUuid,
  });
  posthog.group('organization', organizationUuid, properties);
};

export const clearOrganization = () => {
  if (!isInitialized) {
    return;
  }

  posthog.unregister('organization_uuid');
  posthog.unregister('organization_name');
  posthog.resetGroups();
};

export const resetAnalytics = () => {
  if (!isInitialized) {
    return;
  }

  posthog.reset();
};
